import { FC } from 'react';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useCreatePortalSessionMutation } from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { CalendarToday, CreditCard } from '@mui/icons-material';
import {
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Theme, SxProps } from '@mui/material/styles';

type SubscriptionStatusTypes = 'active' | 'trialing' | 'past_due' | 'canceled' | undefined;
type SubscriptionStatusColor = 'success' | 'info' | 'warning' | 'default';
type SubscriptionStatusLabel = 'Active' | 'Trial' | 'Past Due' | 'Canceled' | string;

type SubscriptionStatusProps = Readonly<{
  subscription: {
    hasActiveSubscription: boolean;
    subscriptionId?: string;
    status?: SubscriptionStatusTypes;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd?: boolean;
    planName?: string;
  };
}>;

const STRINGS = appStrings.modules.subscription.subscriptionStatus;

export const SubscriptionStatus: FC<SubscriptionStatusProps> = ({ subscription }) => {
  const [createPortalSession, { isLoading: isCreatingPortalSession, error: portalError }] =
    useCreatePortalSessionMutation();

  const handleManageBilling = async () => {
    try {
      const returnUrl = `${GROW_URL}${APP_ROUTE.SETTINGS}`;
      const response = await createPortalSession({ returnUrl }).unwrap();

      window.location.href = response.url;
    } catch (error) {
      console.error('Failed to create portal session:', error);
    }
  };

  const getStatusColor = (status?: SubscriptionStatusTypes): SubscriptionStatusColor => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: SubscriptionStatusTypes): SubscriptionStatusLabel => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      default:
        return status || 'Unknown';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not available';

    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!subscription.hasActiveSubscription) {
    return null;
  }

  return (
    <Card sx={statusCardSx}>
      <CardContent sx={cardContentSx}>
        <Box sx={headerSx}>
          <Typography variant="h5" sx={titleSx}>
            Current Subscription
          </Typography>
          <Chip
            label={getStatusLabel(subscription.status)}
            color={getStatusColor(subscription.status)}
            sx={statusChipSx}
          />
        </Box>

        {subscription.cancelAtPeriodEnd && (
          <Alert severity="warning" sx={alertSx}>
            {STRINGS.cancelAtPeriodEnd(formatDate(subscription.currentPeriodEnd))}
          </Alert>
        )}

        {portalError && (
          <Alert severity="error" sx={alertSx}>
            {STRINGS.portalError}
          </Alert>
        )}

        <Grid container spacing={3} sx={infoGridSx}>
          <Grid item xs={12} sm={6}>
            <Box sx={infoItemSx}>
              <Box sx={infoIconSx}>
                <CreditCard sx={iconSx} />
              </Box>
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  {STRINGS.plan}
                </Typography>
                <Typography variant="body1" sx={valueSx}>
                  {subscription.planName || STRINGS.foundationsPlan}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={infoItemSx}>
              <Box sx={infoIconSx}>
                <CalendarToday sx={iconSx} />
              </Box>
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  {subscription.cancelAtPeriodEnd ? STRINGS.cancelsOn : STRINGS.nextBillingDate}
                </Typography>
                <Typography variant="body1" sx={valueSx}>
                  {formatDate(subscription.currentPeriodEnd)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={dividerSx} />

        <Box sx={actionsSx}>
          <Button
            id="subscription-manage-billing-button"
            hierarchy="Primary"
            size="lg"
            onClick={handleManageBilling}
            disabled={isCreatingPortalSession}
            startIcon={isCreatingPortalSession ? <CircularProgress size={16} /> : <CreditCard />}
          >
            {isCreatingPortalSession ? STRINGS.openingPortal : STRINGS.manageBilling}
          </Button>
          <Typography variant="body2" sx={helpTextSx}>
            {STRINGS.updatePaymentInfo}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const statusCardSx: SxProps<Theme> = ({ backrs }) => ({
  border: `2px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-sm'],
});

const cardContentSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-3xl'],
  '&:last-child': {
    paddingBottom: backrs.spacing['spacing-3xl'],
  },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: backrs.spacing['spacing-xl'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  fontWeight: 600,
  color: backrs.colors['Colors/Text/text-primary'],
});

const statusChipSx: SxProps<Theme> = () => ({
  fontWeight: 600,
  fontSize: '0.875rem',
});

const alertSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-xl'],
});

const infoGridSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-xl'],
});

const infoItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const infoIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  flexShrink: 0,
});

const iconSx: SxProps<Theme> = ({ backrs }) => ({
  fontSize: '1.25rem',
  color: backrs.colors['Colors/Text/text-secondary'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  fontWeight: 500,
  color: backrs.colors['Colors/Text/text-secondary'],
  marginBottom: backrs.spacing['spacing-xs'],
});

const valueSx: SxProps<Theme> = ({ backrs }) => ({
  fontWeight: 600,
  color: backrs.colors['Colors/Text/text-primary'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: backrs.spacing['spacing-xl'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const helpTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'center',
  fontSize: '0.875rem',
});
